import { Grid } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router';
import * as emailReportApi from '../../api/app/emailReportApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { PageHeader } from '../../components/core/PageHeader/PageHeader';
import { useTranslation } from '../../contexts/translation';
import { EmailReportInputs } from '../../types/app/emailReport.types';
import { convertDataFromInputToCreateRequest } from '../../utils/core/functions/emailReport.functions';
import { paths } from '../../utils/core/routes';
import { EmailReportForm } from './EmailReportForm';

export function EmailReportCreate() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { mutate: mutateCreateEmailReport } = useMutation(emailReportApi.createEmailReport);
  const [selectedTz, setSelectedTz] = useState<string>();
  const [defaultValues, setDefaultValues] = useState<EmailReportInputs | undefined>(undefined);
  const {
    data: defaultTimeZone,
    isLoading: defaultTimeZonesIsLoading,
    isError: defaultTimeZonesIsError,
  } = useQuery<string>(['defaultTimeZone'], () => emailReportApi.getDefaultTimeZone());

  useEffect(() => {
    if (!defaultTimeZone) return;
    setSelectedTz(defaultTimeZone);
    setDefaultValues({
      timeZone: defaultTimeZone,
    });
  }, [defaultTimeZone]);

  const handleOnSubmit: SubmitHandler<EmailReportInputs> = (data: EmailReportInputs) => {
    const requestData = convertDataFromInputToCreateRequest({
      ...data,
      timeZone: selectedTz ? selectedTz : '',
    });

    mutateCreateEmailReport(
      { ...requestData },
      {
        onSuccess: () => {
          enqueueSnackbar(t('page.email-report.item-created'), { variant: 'success' });
          navigate(paths.EMAIL_REPORT_LIST);
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (defaultTimeZonesIsError) return <ApiError />;
  if (defaultTimeZonesIsLoading || !defaultTimeZone || !selectedTz) return <Loader />;

  return (
    <>
      <PageHeader title={t('page.email-report.headline-create')} />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <EmailReportForm
              onSubmit={handleOnSubmit}
              onBack={() => navigate(paths.EMAIL_REPORT_LIST)}
              selectedTz={selectedTz}
              setSelectedTz={setSelectedTz}
              defaultValues={defaultValues}
            />
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
